<!--
  HOME — a banner, what you were in the middle of, and then the shelf.

  THE BANNER IS A SLIDESHOW OF FIVE (item 10). It cycles slowly through what is
  popular right now, films and series alternating, and it holds the moment you
  put a pointer on it, tab into it, leave the tab, or ask it to. Every rule it
  follows is written down in `Hero.svelte`; the only decision made here is WHICH
  titles it gets, which is `heroPicks()` over the two popular catalogues — one
  request each, and they are the same two requests the first rails make, so the
  banner costs no extra round trip.

  WHAT YOU DID NOT FINISH IS NOT ON THIS PAGE. It used to sit directly under the
  banner, on the argument that "carry on with the thing I started" is the usual
  question on a sofa. The owner's answer is that an unfinished film is not an
  invitation, it is a reminder — and it already has a home in My Jovii, which is
  the one place it should live. Home opens on what there is to watch, not on what
  you abandoned. `MyJovi.svelte` owns Continue watching now, alone.

  THE ROWS BELOW ARE SEEDED, NOT SHUFFLED (item 11). `homeRails()` takes the day
  as a seed, so the page is varied without being different every time you press
  Back — see `shelf.ts` for why that distinction is the whole feature. There is
  no recommendation engine here and this screen does not pretend there is one:
  the two rails drawn from what you kept say so, by name, on the heading.

  Every rail below the first is a `LazyRail` — it fetches when it comes near the
  viewport, so opening Home is two catalogue requests and not fourteen.
-->
<script lang="ts">
  import type { CatalogItem } from '$types';
  import Empty from '$lib/ui/Empty.svelte';
  import Hero from '$lib/ui/Hero.svelte';
  import LazyRail from '$lib/ui/LazyRail.svelte';
  import Poster from '$lib/ui/Poster.svelte';
  import Rail from '$lib/ui/Rail.svelte';
  import { catalog } from '$lib/ui/cinemeta';
  import { continueWatching, recent, saved, progress } from '$lib/ui/prefs.svelte';
  import ContinueCard from '$lib/ui/ContinueCard.svelte';
  import { continueTiles } from '$lib/ui/history-groups';
  /* ONE TILE PER SERIES, not per episode (the owner, 2026-09-13). Three nights
     of one show used to be three tiles and crowded every other title off the
     end of the row. `continueTiles` is `groupHistory` + `historyContinuation`,
     the same pair My Jovii's history sheet uses, so the row and the sheet can
     never disagree about where you are in a show. Films are unchanged. */
  const continuing = $derived(continueTiles(progress.items, continueWatching(Infinity), 8));
  import AwardRail from '$lib/ui/AwardRail.svelte';
  import PlateRail from '$lib/ui/PlateRail.svelte';
  import { daySeed, heroPicks, homeRails, taste } from '$lib/ui/shelf';
  import { COLLECTIONS } from '$lib/ui/collections';
  import { collectionArt, genreArt } from '$lib/ui/browse-art';
  import { genresFor } from '$lib/ui/cinemeta';
  import { browseHref, collectionHref } from '$lib/ui/nav.svelte';

  let films = $state<CatalogItem[]>([]);
  let shows = $state<CatalogItem[]>([]);
  let failed = $state(false);
  let settled = $state(false);
  let answered = $state(false);

  /**
   * THE BANNER IS CHOSEN ONCE, WHEN BOTH CATALOGUES HAVE ANSWERED.
   *
   * Derived straight from the two lists it flipped: whichever request landed
   * first briefly owned the whole banner, so slide one was a series for 200 ms
   * and then a film — and the first slide's backdrop and wordmark, measured at
   * up to 1.1 MB together, had already been fetched for a title nobody saw.
   * Picked once, from whatever has arrived by the time both have answered (or
   * four seconds, so one slow catalogue cannot hold the top of the page).
   */
  let featured = $state<CatalogItem[]>([]);
  let picked = false;
  $effect(() => {
    if (!settled || picked) return;
    /* A wider pool than the banner shows, dealt with the day's seed: six of
       eighteen rather than the same first six every time. */
    const deck = heroPicks([films, shows], 6, seed);
    if (deck.length === 0) return;
    picked = true;
    featured = deck;
  });

  /*
   * The seed is fixed for the life of the screen, not read from the clock on
   * every pass: an evening that runs past midnight should not re-deal the shelf
   * under somebody halfway down it.
   */
  const seed = daySeed();
  const likes = $derived(taste(saved.items, recent.items, 2));
  const rails = $derived(homeRails(seed, likes));

  $effect(() => {
    const abort = new AbortController();
    let down = 0;

    // Both catalogues, in parallel. ONE failing is not a screen failing — the
    // banner and the shelf both work with whichever side answered, so the
    // apology is only drawn when BOTH are down.
    const ask = (kind: 'movie' | 'series', keep: (items: CatalogItem[]) => void): Promise<void> =>
      catalog({ kind, catalog: 'top' }, abort.signal)
        .then((items) => {
          if (!abort.signal.aborted) keep(items);
        })
        .catch((err: unknown) => {
          if ((err as Error)?.name === 'AbortError') return;
          down += 1;
          if (down === 2) failed = true;
        });

    const guard = setTimeout(() => (settled = true), 4000);
    void Promise.all([
      ask('movie', (items) => (films = items)),
      ask('series', (items) => (shows = items))
    ]).then(() => {
      if (!abort.signal.aborted) {
        answered = true;
        settled = true;
      }
    });

    return () => {
      abort.abort();
      clearTimeout(guard);
    };
  });
</script>

<svelte:head><title>Jovii</title></svelte:head>

{#if failed && featured.length === 0}
  <Empty
    icon="alert"
    tone="bad"
    title="The catalogue is not answering"
    detail="Cinemeta could not be reached. Search still works if it comes back; try again in a moment."
  />
{:else}
  <h1 class="home-heading">Find your next watch</h1>
  {#if answered && films.length === 0 && shows.length === 0}
    <Empty icon="search" title="No featured titles yet" detail="The catalogue returned no titles. Try browsing a category or search for a title." />
  {:else}
    <Hero items={featured} />
  {/if}


  <!--
    The ranked row. Titled by what it can actually prove: this is the catalogue's
    own `top` ordering, cut at ten — not a chart Jovii computed and not an award.
    The numeral is the array index, so the heading has to stay honest about it.
  -->
  <!-- `limit={10}`: exactly ten, ranked — and the rail loops its ten, numerals
       riding along in every wrap (the owner, 2026-08-17: every rail loops at
       its category's end; this rail's category IS the ten). -->
  <LazyRail title="Popular shows" kind="series" catalog="top" limit={10} ranked eager />

  <!--
    CONTINUE WATCHING, where "A place to start" used to be. The owner (dock
    review): "get rid of A Place to Start and replace it with Continue Watching,
    so people can click it and continue from exactly where they left off."
    The cards resume at the stored position (ContinueCard -> watchHref with the
    resume point; the player reads `resumeFor`), so "exactly" is literal. It
    used to sit above the ranked row; the owner wants it in this slot, below.
    Nothing when there is nothing to continue - an empty shelf with a prompt in
    it is the thing that was just removed.
  -->
  {#if continuing.length > 0}
    <Rail title="Continue watching" note="Pick up where you left off." wide href="/profile">
      {#each continuing as tile (tile.item.key)}<ContinueCard item={tile.item} forgetKeys={tile.keys} eager />{/each}
    </Rail>
  {/if}

  {#each rails as rail, i (rail.key)}
    <LazyRail
      title={rail.title}
      note={rail.note}
      kind={rail.kind}
      catalog={rail.catalog}
      genre={rail.genre}
      eager={i === 0}
    />
  {/each}

  <!--
    THE PRIZES. Data from Wikidata (CC0), marks of our own — see
    `lib/ui/awards.ts` for why the statuettes are not here. Each rail leads
    with the newest winners; some pool sibling categories so the row actually
    fills (the Globes split by drama/comedy, the festivals by city).
  -->
  <AwardRail award="oscar" title="Best Picture winners" />
  <AwardRail
    award={['emmy-drama', 'emmy-comedy', 'emmy-limited', 'emmy-any']}
    title="Emmy winners"
    note="Primetime Emmy winners"
  />
  <AwardRail
    award={['globe', 'globe-comedy', 'globe-tv-drama', 'globe-tv-comedy', 'globe-limited']}
    title="Golden Globe winners"
    note="Best film and best series, drama and comedy"
  />
  <AwardRail
    award={['palme', 'lion', 'bear']}
    title="Festival favourites"
    note="Palme d'Or, Cannes · Golden Lion, Venice · Golden Bear, Berlin"
  />
  <AwardRail
    award="oscar-animated"
    title="Animated winners"
    note="Winners of the Academy Award for Best Animated Feature"
  />
  <AwardRail award="bafta" title="BAFTA Best Film winners" />

  <!--
    THE WAYS INTO THE REST OF IT, at the foot of every browse screen.
    Collections first because they are opinionated and short; the genre rows
    after, because those are the exhaustive lists and an exhaustive list is
    what you reach for when the opinionated one did not have it. Genres are
    TWO rows, one per kind, so a tile always says which shelf it opens —
    the single mixed row sent people to films they thought were series.
    Every tile carries our own generated still (see lib/ui/browse-art.ts);
    the two genre rows deliberately share one image per genre.
  -->
  <PlateRail
    title="Browse by collection"
    plates={COLLECTIONS.map((c) => ({
      href: collectionHref(c.slug),
      label: c.label,
      note: c.note,
      image: collectionArt(c.slug)
    }))}
  />

  <PlateRail
    title="Films by genre"
    plates={genresFor('movie').map((g) => ({
      href: browseHref('movie', g),
      label: g,
      image: genreArt(g)
    }))}
  />

  <PlateRail
    title="Series by genre"
    plates={genresFor('series').map((g) => ({
      href: browseHref('series', g),
      label: g,
      image: genreArt(g)
    }))}
  />

  {#if recent.items.length > 0}
    <Rail title="Recently opened">
      {#each recent.items as item (item.id)}
        <Poster {item} />
      {/each}
    </Rail>
  {/if}
{/if}

<style>
  .home-heading { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip-path: inset(50%); white-space: nowrap; }
</style>
